import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';

import {
  getPublicProformaPayment,
  initiatePublicProformaCardPayment,
  submitPublicProformaOfflinePayment,
  downloadPublicProformaTaxInvoicePdf,
  downloadPublicProformaBillingInvoicePdf,
  downloadPublicProformaPaymentReceiptPdf,
  getPublicEventSettings,
} from '../../../services/eventApi';
import { uploadFilePublic } from '../../../services/fileApi';
import PublicSlimHeader from '../../Common/PublicSlimHeader';
import { eventAllowsIndustryLinking } from '../../../utils/eventIndustryLinking';
import ProofAttachmentDropzone from '../../Common/ProofAttachmentDropzone';
import { formatMarketMoney } from '../../../utils/eventPricingDisplay';
import { EventTableSerialTh, EventTableSerialTd } from '../Common/EventTableSerial';
import { PublicPaymentPageLoading } from '../Common/eventPublicPaymentUi';

const money = (n, pricingMarket) => formatMarketMoney(n, pricingMarket || 'LOCAL');

const bytesToLabel = (size) => {
  const v = Number(size || 0);
  if (!v) return '';
  if (v >= 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(2)} MB`;
  return `${Math.ceil(v / 1024)} KB`;
};

/** Labels for application table: nominee(s), contact email, category, industry. */
function getProformaSubmissionRowLabels(s) {
  const people = Array.isArray(s?.people) ? s.people.filter(Boolean) : [];
  const names = people.map((p) => String(p?.fullName || '').trim()).filter(Boolean);
  const emails = people.map((p) => String(p?.email || '').trim()).filter(Boolean);
  const nomineeName = names.length ? names.join(', ') : String(s?.submitterName || '').trim() || '—';
  const nomineeEmail = emails.length ? emails.join(', ') : String(s?.submitterEmail || '').trim() || '—';

  let categoryLabel = String(s?.categoryName || '').trim();
  if (!categoryLabel && s?.category?.name) categoryLabel = String(s.category.name).trim();
  if (!categoryLabel && people[0]?.categoryId?.name) categoryLabel = String(people[0].categoryId.name).trim();
  if (!categoryLabel) {
    const lines = Array.isArray(s?.pricingSnapshot?.categoryLines) ? s.pricingSnapshot.categoryLines : [];
    const first = lines.find((l) => String(l?.categoryName || '').trim());
    if (first) categoryLabel = String(first.categoryName).trim();
  }

  let industryLabel = String(s?.industryName || '').trim();
  if (!industryLabel && s?.industry?.name) industryLabel = String(s.industry.name).trim();

  return {
    nomineeName,
    nomineeEmail,
    categoryLabel: categoryLabel || '—',
    industryLabel: industryLabel || '—',
  };
}
const defaultPaymentTerms = [
  'Payment can be made via Cash, Cheque, or Bank Transfer.',
  'Full payment must be completed on or before the stated payment deadline.',
  'Payments made after the deadline may result in delays or non-confirmation of the application.',
  'All payments once made are non-refundable and non-transferable after the Tax Invoice is issued.',
  'If payment is made via bank transfer, a clear copy of the payment receipt/slip must be shared with the organizing team for verification.',
  'Applications will be confirmed only after successful payment verification.',
  'Any bank charges or transfer fees must be borne by the applicant.',
  'Invoice details cannot be changed after the Tax Invoice has been issued.',
  'SLIM reserves the right to cancel or reject applications that do not comply with the payment requirements.',
  'Applicants should keep a copy of the payment proof for future reference.',
];

const BANK_ROW_KEYS = [
  'beneficiaryName',
  'beneficiaryBank',
  'bankCode',
  'beneficiaryBranch',
  'branchCode',
  'accountNumber',
];

const bankTransferRowComplete = (d) =>
  d && BANK_ROW_KEYS.every((k) => String(d[k] || '').trim());

const ProformaPaymentInstructions = ({ paymentDisplay, bankAccount }) => {
  const pd = paymentDisplay || {};
  const d = pd.bankTransferDetails || {};
  const leg = pd.ledgerBank || {};
  const card = pd.cardClearingAccount || {};
  const ledgerLine = [bankAccount?.code, bankAccount?.name].filter(Boolean).join(' – ')
    || [leg.code, leg.name].filter(Boolean).join(' – ');

  return (
    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      <div className="px-4 sm:px-5 py-3 border-b border-gray-200">
        <h2 className="text-base sm:text-lg font-semibold text-gray-900">Payment instructions</h2>
        <p className="mt-1 text-xs text-gray-600">
          Use these details for bank transfer or cheque. Card payments use the button below.
        </p>
      </div>
      <div className="px-4 sm:px-5 py-4 space-y-4 text-sm">
        {pd.chequePayeeName ? (
          <p className="text-gray-800">
            <span className="font-medium text-gray-900">Cheques:</span> Payable to{' '}
            <span className="font-semibold">{pd.chequePayeeName}</span>
          </p>
        ) : null}

        {bankTransferRowComplete(d) ? (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs border border-gray-200 rounded-md overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-2 text-left font-semibold text-gray-700 border-b">Beneficiary</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-700 border-b">Bank</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-700 border-b">Codes</th>
                  <th className="px-2 py-2 text-left font-semibold text-gray-700 border-b">Account</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="px-2 py-2 text-gray-800 border-b border-gray-100 align-top">
                    <div>{d.beneficiaryName}</div>
                    <div className="text-gray-500">{d.beneficiaryBranch}</div>
                  </td>
                  <td className="px-2 py-2 text-gray-800 border-b border-gray-100 align-top">{d.beneficiaryBank}</td>
                  <td className="px-2 py-2 text-gray-800 border-b border-gray-100 align-top">
                    <div>Bank: {d.bankCode}</div>
                    <div>Branch: {d.branchCode}</div>
                  </td>
                  <td className="px-2 py-2 text-gray-900 font-mono border-b border-gray-100 align-top">{d.accountNumber}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : ledgerLine ? (
          <p className="text-gray-700">
            <span className="font-medium text-gray-900">Ledger bank / cash:</span> {ledgerLine}
          </p>
        ) : (
          <p className="text-amber-800 text-sm">Bank transfer details are not configured. Please contact the event team.</p>
        )}

        {(card.code || card.name) && (
          <p className="text-gray-700">
            <span className="font-medium text-gray-900">Card payments:</span> Settlements are recorded against{' '}
            {[card.code, card.name].filter(Boolean).join(' – ')}.
          </p>
        )}
        {pd.cardPaymentNote ? (
          <p className="text-gray-700 whitespace-pre-wrap border-t border-gray-100 pt-3">{pd.cardPaymentNote}</p>
        ) : null}
      </div>
    </div>
  );
};

const statusPillClass = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'PAID') return 'bg-emerald-50 text-emerald-800 border-emerald-200';
  if (s === 'PENDING_APPROVAL') return 'bg-amber-50 text-amber-800 border-amber-200';
  if (s === 'ACTIVE') return 'bg-indigo-50 text-indigo-800 border-indigo-200';
  if (s === 'REJECTED') return 'bg-red-50 text-red-800 border-red-200';
  return 'bg-gray-50 text-gray-700 border-gray-200';
};

const PAYMENT_LINK_DISPLAY = {
  NOT_REQUESTED: 'Not requested',
  ACTIVE: 'Active',
  PAID: 'Paid',
  EXPIRED: 'Expired',
  CANCELLED: 'Cancelled',
};

function isOfflineProformaPaymentMethod(method) {
  const m = String(method || '').toUpperCase();
  return m === 'BANK_TRANSFER' || m === 'CHEQUE_DEPOSIT';
}

function formatPublicPaymentStatusPillLabel(data) {
  const paySt = String(data?.paymentStatus || 'UNPAID').toUpperCase();
  const method = String(data?.paymentMethod || '').toUpperCase();
  if (paySt === 'PENDING_APPROVAL' && isOfflineProformaPaymentMethod(method)) return 'Hold';
  const map = {
    PENDING_APPROVAL: 'Pending approval',
    PAID: 'Paid',
    UNPAID: 'Unpaid',
    REJECTED: 'Rejected',
    REFUNDED: 'Refunded',
  };
  return map[paySt] || String(data?.paymentStatus || 'Unpaid');
}

function formatPublicPaymentLinkPillLabel(linkStatus) {
  const u = String(linkStatus || 'NOT_REQUESTED').toUpperCase();
  return PAYMENT_LINK_DISPLAY[u] || String(linkStatus || 'Not requested');
}

const formatPaymentMethodLabel = (m) => {
  const v = String(m || '').toUpperCase();
  if (v === 'CARD') return 'Card payment (Paycorp)';
  if (v === 'BANK_TRANSFER') return 'Bank transfer';
  if (v === 'CHEQUE_DEPOSIT') return 'Cheque deposit';
  return m || 'Offline payment';
};

const formatPaymentDate = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const SubmittedDetailRow = ({ label, value, mono = false }) => (
  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 text-sm">
    <dt className="col-span-1 text-gray-600">{label}</dt>
    <dd className={`col-span-2 sm:col-span-3 text-gray-900 ${mono ? 'font-mono break-all' : ''}`}>
      {value || '—'}
    </dd>
  </div>
);

const PaymentConfirmedCard = ({ data, onDownloadTaxInvoice, onDownloadBillingInvoice, onDownloadReceipt }) => (
  <div className="bg-white border-l-4 border-emerald-500 border border-emerald-200 rounded-xl shadow-sm p-4 sm:p-5">
    <div className="flex items-start gap-3">
      <div className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-emerald-100 text-emerald-700">
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <h2 className="text-base sm:text-lg font-semibold text-emerald-900">Payment confirmed</h2>
        <p className="mt-1 text-sm text-emerald-900/80">
          Your payment has been received and your applications are now confirmed for this event.
        </p>
        <dl className="mt-4 space-y-1.5">
          <SubmittedDetailRow label="Method" value={formatPaymentMethodLabel(data?.paymentMethod)} />
          <SubmittedDetailRow label="Amount" value={money(data?.paymentAmount || data?.invoice?.grandTotal, data?.pricingMarket)} />
          <SubmittedDetailRow label="Paid on" value={formatPaymentDate(data?.paidAt)} />
          {data?.paymentReferenceNumber ? (
            <SubmittedDetailRow label="Reference" value={data.paymentReferenceNumber} mono />
          ) : null}
        </dl>
        <div className="mt-4 flex flex-wrap gap-2">
          {onDownloadReceipt && (
            <button
              type="button"
              onClick={onDownloadReceipt}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-violet-600 text-white text-sm font-medium hover:bg-violet-700"
            >
              Download Payment Receipt
            </button>
          )}
          {data?.taxInvoice?.invoiceNumber && onDownloadTaxInvoice && (
            <button
              type="button"
              onClick={onDownloadTaxInvoice}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download Tax Invoice ({data.taxInvoice.invoiceNumber})
            </button>
          )}
          {data?.billingInvoice?.invoiceNumber && onDownloadBillingInvoice && (
            <button
              type="button"
              onClick={onDownloadBillingInvoice}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-sky-600 text-white text-sm font-medium hover:bg-sky-700"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download Invoice ({data.billingInvoice.invoiceNumber})
            </button>
          )}
        </div>
      </div>
    </div>
  </div>
);

const PaymentPendingApprovalCard = ({ data }) => {
  const receipts = Array.isArray(data?.receiptDocuments) ? data.receiptDocuments : [];
  return (
    <div className="bg-amber-50 border-l-4 border-amber-500 border border-amber-200 rounded-xl shadow-sm p-4 sm:p-5">
      <div className="flex items-start gap-3">
        <div className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-amber-100 text-amber-700">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-base sm:text-lg font-semibold text-amber-900">Payment submitted — awaiting approval</h2>
          <p className="mt-1 text-sm text-amber-900/90">
            We&apos;ve received your payment details. Our team will verify them and approve the payment shortly.
            You&apos;ll get a confirmation email at <span className="font-semibold">{data?.coordinatorEmail || 'your registered address'}</span> once it&apos;s approved.
          </p>
          <dl className="mt-4 space-y-1.5">
            <SubmittedDetailRow label="Method" value={formatPaymentMethodLabel(data?.paymentMethod)} />
            <SubmittedDetailRow label="Reference" value={data?.paymentReferenceNumber} mono />
            <SubmittedDetailRow label="Payment date" value={formatPaymentDate(data?.paymentDate)} />
            <SubmittedDetailRow label="Amount" value={money(data?.paymentAmount || data?.invoice?.grandTotal, data?.pricingMarket)} />
          </dl>
          {receipts.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-amber-900 uppercase tracking-wide">Receipts uploaded</p>
              <ul className="mt-1 space-y-1">
                {receipts.map((r, idx) => (
                  <li key={r.fileId || `${r.fileName}-${idx}`} className="text-sm text-amber-900 flex items-center gap-2">
                    <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="truncate">{r.originalName || r.fileName || 'Receipt'}</span>
                    {r.fileSize ? (
                      <span className="text-amber-800/70 text-xs whitespace-nowrap">({bytesToLabel(r.fileSize)})</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="mt-4 text-xs text-amber-900/80">
            You can safely close this page now. If approval is taking longer than expected, contact the event organiser.
          </p>
        </div>
      </div>
    </div>
  );
};

const PaymentRejectedCard = ({ data }) => (
  <div className="bg-red-50 border-l-4 border-red-500 border border-red-200 rounded-xl shadow-sm p-4 sm:p-5">
    <div className="flex items-start gap-3">
      <div className="shrink-0 inline-flex items-center justify-center w-9 h-9 rounded-full bg-red-100 text-red-700">
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <h2 className="text-base sm:text-lg font-semibold text-red-900">Previous payment was rejected</h2>
        {data?.rejectionReason ? (
          <p className="mt-1 text-sm text-red-900/90 whitespace-pre-wrap">
            <span className="font-semibold">Reason:</span> {data.rejectionReason}
          </p>
        ) : (
          <p className="mt-1 text-sm text-red-900/90">
            Your previous payment submission was rejected by the event organiser. Please re-submit using the form below.
          </p>
        )}
        {data?.rejectedAt ? (
          <p className="mt-1 text-xs text-red-900/70">Rejected on {formatPaymentDate(data.rejectedAt)}</p>
        ) : null}
        <p className="mt-3 text-sm text-red-900/90">
          You can submit a corrected payment using the form below.
        </p>
      </div>
    </div>
  </div>
);

const PublicProformaPaymentPage = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [data, setData] = useState(null);
  const [method, setMethod] = useState('CARD');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [receipts, setReceipts] = useState([]);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [paymentAgreementText, setPaymentAgreementText] = useState('');

  const allowIndustryLinking = useMemo(
    () => eventAllowsIndustryLinking(data?.event),
    [data?.event]
  );

  const load = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await getPublicProformaPayment(token);
      const raw = res?.data;
      const nested = raw?.data;
      const payload =
        nested && typeof nested === 'object'
          ? nested
          : raw && typeof raw === 'object' && (raw.requestNumber != null || raw.requestId != null)
            ? raw
            : null;
      setData(payload);
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Could not load payment page';
      toast.error(msg);
      setLoadError(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  /** Narrow dependency so the effect is not re-triggered when `useSearchParams` returns a new object reference with the same query. */
  const paycorpCallbackStatus = String(searchParams.get('paycorp') || '').toLowerCase();

  useEffect(() => {
    getPublicEventSettings()
      .then((res) => setPaymentAgreementText(String(res.data?.data?.paymentAgreementText || '').trim()))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!paycorpCallbackStatus) return;
    // Stable ids so double effect runs (e.g. React Strict Mode) do not show duplicate toasts.
    if (paycorpCallbackStatus === 'success') {
      toast.success('Card payment completed successfully', { id: 'public-proforma-paycorp-success' });
    } else if (paycorpCallbackStatus === 'failed') {
      toast.error('Card payment failed', { id: 'public-proforma-paycorp-failed' });
    } else if (paycorpCallbackStatus === 'cancelled') {
      toast.error('Card payment was cancelled', { id: 'public-proforma-paycorp-cancelled' });
    }
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      // After the gateway redirect, take the user back to the top so the
      // success / failure banner is the first thing they see.
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [paycorpCallbackStatus]);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      setLoadError('Payment link is missing from the URL.');
      setData(null);
      return;
    }
    load();
  }, [token]);

  const isPaid = String(data?.paymentStatus || '').toUpperCase() === 'PAID';
  const isPendingApproval = String(data?.paymentStatus || '').toUpperCase() === 'PENDING_APPROVAL';
  const isRejected = String(data?.paymentStatus || '').toUpperCase() === 'REJECTED';
  const invoiceTotal = useMemo(() => Number(data?.invoice?.grandTotal || 0), [data]);
  const amountPaid = useMemo(() => Number(data?.amountPaid || 0), [data]);
  const amountDue = useMemo(() => Number(data?.amountDue || 0), [data]);
  const total = amountDue > 0 && !isPaid ? amountDue : invoiceTotal;
  const canProceedPayment =
    acceptedTerms && !submitting && !isPaid && !isPendingApproval;
  const canSubmitOffline =
    canProceedPayment &&
    (method === 'BANK_TRANSFER' || method === 'CHEQUE_DEPOSIT') &&
    Boolean(referenceNumber.trim()) &&
    Boolean(String(paymentDate || '').trim()) &&
    receipts.length > 0;

  const onPickReceipt = async (fileOrEvent) => {
    const file = fileOrEvent?.target?.files?.[0] || fileOrEvent;
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      if (fileOrEvent?.target) fileOrEvent.target.value = '';
      return;
    }
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(String(file.type || '').toLowerCase())) {
      toast.error('Only PDF, JPG, and PNG files are allowed');
      if (fileOrEvent?.target) fileOrEvent.target.value = '';
      return;
    }
    setSubmitting(true);
    try {
      const uploadRes = await uploadFilePublic(file, {
        category: 'Payment Receipt',
        description: `Proforma payment receipt: ${data?.requestNumber || ''}`.trim(),
        title: file.name,
      });
      const f = uploadRes?.data?.data || uploadRes?.data || {};
      setReceipts((prev) => [
        ...prev,
        {
          fileId: f.fileId || f._id || '',
          fileName: f.fileName || file.name,
          originalName: f.originalName || file.name,
          fileSize: f.fileSize || file.size,
          fileType: f.fileType || file.type,
        },
      ]);
      toast.success('Receipt uploaded');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Receipt upload failed');
    } finally {
      setSubmitting(false);
      if (fileOrEvent?.target) {
        fileOrEvent.target.value = '';
      }
    }
  };

  const handleCard = async () => {
    if (!acceptedTerms) return toast.error('Please accept the payment terms and conditions before paying');
    setSubmitting(true);
    try {
      const res = await initiatePublicProformaCardPayment(token, {
        amount: total,
        paymentAgreement: {
          agreed: true,
          agreedAtClient: new Date().toISOString(),
          source: 'public_proforma_payment_page',
        },
      });
      const url = res?.data?.data?.paymentPageUrl;
      if (!url) throw new Error('Gateway url missing');
      window.location.href = url;
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Could not start card payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOfflineSubmit = async () => {
    if (!acceptedTerms) return toast.error('Please accept the payment terms and conditions before submitting');
    if (!referenceNumber.trim()) return toast.error('Reference number is required');
    if (!String(paymentDate || '').trim()) return toast.error('Payment date is required');
    if (!receipts.length) return toast.error('At least one receipt is required');
    setSubmitting(true);
    try {
      await submitPublicProformaOfflinePayment(token, {
        method,
        amount: total,
        referenceNumber: referenceNumber.trim(),
        paymentDate: paymentDate.trim(),
        receiptDocuments: receipts,
        paymentAgreement: {
          agreed: true,
          agreedAtClient: new Date().toISOString(),
          source: 'public_proforma_payment_page',
        },
      });
      toast.success('Payment submitted for approval');
      // Clear the local form state so the user doesn't see their inputs lingering
      // when the page transitions into the "pending approval" view after reload.
      setReferenceNumber('');
      setPaymentDate('');
      setReceipts([]);
      setAcceptedTerms(false);
      await load();
      // Surface the new status banner at the top of the viewport.
      if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not submit offline payment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownloadBillingInvoice = async () => {
    try {
      const res = await downloadPublicProformaBillingInvoicePdf(token);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeNo = String(data?.billingInvoice?.invoiceNumber || data?.requestNumber || 'invoice').replace(/[/\\?%*:|"<>]/g, '-');
      a.href = url;
      a.download = `${safeNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not download invoice');
    }
  };

  const handleDownloadTaxInvoice = async () => {
    try {
      const res = await downloadPublicProformaTaxInvoicePdf(token);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeNo = String(data?.taxInvoice?.invoiceNumber || data?.requestNumber || 'tax-invoice').replace(/[/\\?%*:|"<>]/g, '-');
      a.href = url;
      a.download = `${safeNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not download tax invoice');
    }
  };

  const handleDownloadReceipt = async () => {
    try {
      const res = await downloadPublicProformaPaymentReceiptPdf(token, 'latest');
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safeNo = String(data?.requestNumber || 'receipt').replace(/[/\\?%*:|"<>]/g, '-');
      a.href = url;
      a.download = `Receipt_${safeNo}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Could not download payment receipt');
    }
  };

  if (loading) {
    return <PublicPaymentPageLoading />;
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50 p-6 flex items-center justify-center">
        <div className="bg-white border border-red-200 rounded-xl px-6 py-5 shadow-sm max-w-md text-red-700 space-y-2">
          <p className="font-medium">Payment link is invalid or could not be loaded.</p>
          {loadError ? <p className="text-sm text-red-600/90 whitespace-pre-wrap">{loadError}</p> : null}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-blue-50 to-purple-50 py-6 px-3 sm:px-6">
      <div className="max-w-6xl mx-auto space-y-4">
        <PublicSlimHeader
          title={data.event?.name || 'SLIM Event'}
          subtitle={`Proforma Invoice Payment • ${data.requestNumber || '—'}`}
          logoAlt="SLIM logo"
          logoClassName="h-11 w-auto object-contain"
          rightLogoSrc={data.eventLogoUrl || ''}
          rightLogoAlt={data.event?.name ? `${data.event.name} logo` : 'Event logo'}
          rightLogoClassName="h-14 w-14 rounded-full object-contain border border-amber-200 bg-white p-1"
        />

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 sm:p-5">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Event Information</h2>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-1.5 text-sm">
                <p className="text-gray-700"><span className="font-medium text-gray-900">Event:</span> {data.event?.name || '—'}</p>
                <p className="text-gray-700"><span className="font-medium text-gray-900">Event code:</span> {data.event?.code || '—'}</p>
                <p className="text-gray-700"><span className="font-medium text-gray-900">Company / Agency:</span> {data.companyName || '—'}</p>
                <p className="text-gray-700"><span className="font-medium text-gray-900">Coordinator:</span> {data.coordinatorName || '—'}</p>
                <p className="text-gray-700 sm:col-span-2"><span className="font-medium text-gray-900">Coordinator email:</span> {data.coordinatorEmail || '—'}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${statusPillClass(data.paymentStatus)}`}>
                Payment: {formatPublicPaymentStatusPillLabel(data)}
              </span>
              <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border ${statusPillClass(data.paymentLinkStatus)}`}>
                Link: {formatPublicPaymentLinkPillLabel(data.paymentLinkStatus)}
              </span>
            </div>
          </div>
          {data?.taxInvoice?.generatedAt && data?.taxInvoice?.invoiceNumber && (
            <div className="mt-3">
              <button
                type="button"
                onClick={handleDownloadTaxInvoice}
                className="px-3 py-1.5 rounded-md border border-indigo-200 bg-indigo-50 text-indigo-800 text-sm hover:bg-indigo-100"
              >
                Download Tax Invoice ({data.taxInvoice.invoiceNumber})
              </button>
            </div>
          )}
          {data?.billingInvoice?.generatedAt && data?.billingInvoice?.invoiceNumber && (
            <div className="mt-3">
              <button
                type="button"
                onClick={handleDownloadBillingInvoice}
                className="px-3 py-1.5 rounded-md border border-sky-200 bg-sky-50 text-sky-800 text-sm hover:bg-sky-100"
              >
                Download Invoice ({data.billingInvoice.invoiceNumber})
              </button>
            </div>
          )}
        </div>

        {/* Card payment confirmed (also fired by paycorp callback) */}
        {isPaid && (
          <PaymentConfirmedCard
            data={data}
            onDownloadTaxInvoice={data?.taxInvoice?.invoiceNumber ? handleDownloadTaxInvoice : null}
            onDownloadBillingInvoice={data?.billingInvoice?.invoiceNumber ? handleDownloadBillingInvoice : null}
            onDownloadReceipt={handleDownloadReceipt}
          />
        )}

        {/* Offline payment submitted, waiting for staff approval */}
        {isPendingApproval && (
          <PaymentPendingApprovalCard data={data} />
        )}

        {/* Previous offline payment was rejected — banner + reason; the
            Make Payment form below stays available so the coordinator can
            submit corrected details. */}
        {isRejected && (
          <PaymentRejectedCard data={data} />
        )}

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 sm:px-5 py-3 border-b border-gray-200">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Invoice Breakdown</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <EventTableSerialTh className="px-4 py-2.5" />
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">Description</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-gray-700">Quantity</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-gray-700">Rate</th>
                  <th className="px-4 py-2.5 text-right text-xs font-semibold uppercase tracking-wide text-gray-700">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {(data.invoice?.lineItems || []).map((line, idx) => {
                  const qty = Number(line.quantity) || 0;
                  const gross = qty > 0 && Number(line.rate)
                    ? Math.round(qty * Number(line.rate) * 100) / 100
                    : Number(line.amount || 0);
                  const discPct = Number(line.discountPercent || 0);
                  const discAmt = Number(line.discountAmount || 0);
                  const lineDisc = discAmt > 0
                    ? discAmt
                    : (discPct > 0 ? Math.round(gross * (discPct / 100) * 100) / 100 : 0);
                  const amount = line.amount != null && Number(line.amount) !== gross
                    ? Number(line.amount)
                    : (lineDisc > 0 ? Math.max(0, gross - lineDisc) : (Number(line.amount) || gross));
                  const rate = Number(line.rate) || (qty > 0 ? gross / qty : 0);
                  return (
                    <tr key={`${line.description || 'line'}-${idx}`}>
                      <EventTableSerialTd order="desc" rowCount={(data.invoice?.lineItems || []).length} index={idx} className="px-4 py-2.5" />
                      <td className="px-4 py-2.5 text-gray-800">{line.description || 'Line item'}</td>
                      <td className="px-4 py-2.5 text-right text-gray-800">{qty ? qty.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}</td>
                      <td className="px-4 py-2.5 text-right text-gray-800">{money(rate, data?.pricingMarket)}</td>
                      <td className="px-4 py-2.5 text-right font-medium text-gray-900">{money(amount, data?.pricingMarket)}</td>
                    </tr>
                  );
                })}
                {(() => {
                  const computedSubTotal = (data.invoice?.lineItems || []).reduce(
                    (sum, line) => sum + (Number(line.amount) || 0),
                    0
                  ) || Math.max(0, Number(data.invoice?.subTotal || 0) - Number(data.invoice?.totalDiscount || 0)) || Number(data.invoice?.subTotal || 0);
                  return (
                    <tr>
                      <td className="px-4 py-2.5" colSpan={3} />
                      <td className="px-4 py-2.5 text-gray-700">Sub total</td>
                      <td className="px-4 py-2.5 text-right text-gray-800">{money(computedSubTotal, data?.pricingMarket)}</td>
                    </tr>
                  );
                })()}
                <tr>
                  <td className="px-4 py-2.5" colSpan={3} />
                  <td className="px-4 py-2.5 text-gray-700">Tax total</td>
                  <td className="px-4 py-2.5 text-right text-gray-800">{money(data.invoice?.taxTotal, data?.pricingMarket)}</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-4 py-3" colSpan={3} />
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">Grand total</td>
                  <td className="px-4 py-3 text-right text-sm font-semibold text-gray-900">{money(invoiceTotal, data?.pricingMarket)}</td>
                </tr>
                {amountPaid > 0 && (
                  <tr>
                    <td className="px-4 py-2.5" colSpan={3} />
                    <td className="px-4 py-2.5 text-gray-700">Already paid</td>
                    <td className="px-4 py-2.5 text-right text-gray-800">{money(amountPaid, data?.pricingMarket)}</td>
                  </tr>
                )}
                {amountPaid > 0 && amountDue > 0 && (
                  <tr className="bg-amber-50">
                    <td className="px-4 py-3" colSpan={3} />
                    <td className="px-4 py-3 text-sm font-semibold text-amber-950">Additional amount due</td>
                    <td className="px-4 py-3 text-right text-sm font-semibold text-amber-950">{money(amountDue, data?.pricingMarket)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {data.paymentDisplay && (
          <ProformaPaymentInstructions paymentDisplay={data.paymentDisplay} bankAccount={data.bankAccount} />
        )}

        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 sm:px-5 py-3 border-b border-gray-200">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">Applications</h2>
            <p className="mt-1 text-xs text-gray-600">
              Applications included in this proforma invoice. Signed application PDFs, when required, are requested during screening via the external portal.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <EventTableSerialTh className="px-4 py-2.5" />
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">Application #</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">Nominee name</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">Email</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">Category</th>
                  {allowIndustryLinking && (
                    <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-gray-700">Industry</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {(data.submissions || []).map((s, index) => {
                  const row = getProformaSubmissionRowLabels(s);
                  return (
                    <tr key={s._id || s.applicationNumber}>
                      <EventTableSerialTd order="desc" rowCount={(data.submissions || []).length} index={index} className="px-4 py-2.5" />
                      <td className="px-4 py-2.5 font-mono text-gray-900 whitespace-nowrap">{s.applicationNumber || '—'}</td>
                      <td className="px-4 py-2.5 text-gray-800 max-w-[12rem] sm:max-w-xs">{row.nomineeName}</td>
                      <td className="px-4 py-2.5 text-gray-700 break-all max-w-[14rem] sm:max-w-sm">{row.nomineeEmail}</td>
                      <td className="px-4 py-2.5 text-gray-700 max-w-[10rem] sm:max-w-xs">{row.categoryLabel}</td>
                      {allowIndustryLinking && (
                        <td className="px-4 py-2.5 text-gray-700 max-w-[10rem] sm:max-w-xs">{row.industryLabel}</td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {!isPaid && !isPendingApproval && (
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 sm:p-5 space-y-4">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900">
              {amountPaid > 0 && amountDue > 0
                ? 'Pay Additional Balance'
                : isRejected ? 'Re-submit Payment (full amount required)' : 'Make Payment (full amount required)'}
            </h2>
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <label className="flex items-start gap-2 text-sm text-amber-900">
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  I have read and agree to the payment terms and conditions.{' '}
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="underline font-semibold hover:text-amber-950"
                  >
                    Show terms and conditions
                  </button>
                </span>
              </label>
            </div>

            {(!acceptedTerms) && (
              <div className="text-xs rounded-md border border-red-200 bg-red-50 text-red-700 px-3 py-2">
                Payment cannot continue until you accept the payment terms and conditions.
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {[
                { value: 'CARD', label: 'Card (Paycorp)' },
                { value: 'BANK_TRANSFER', label: 'Bank transfer' },
                { value: 'CHEQUE_DEPOSIT', label: 'Cheque deposit' },
              ].map((m) => {
                const checked = method === m.value;
                return (
                  <label
                    key={m.value}
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md border text-sm cursor-pointer transition-colors ${
                      checked
                        ? 'border-indigo-200 bg-indigo-50 text-indigo-800'
                        : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      checked={checked}
                      onChange={() => setMethod(m.value)}
                      className="accent-indigo-600"
                    />
                    {m.label}
                  </label>
                );
              })}
            </div>

            {method === 'CARD' ? (
              <button
                type="button"
                onClick={handleCard}
                disabled={!canProceedPayment}
                className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Redirecting…' : `Pay ${money(total, data?.pricingMarket)} by card`}
              </button>
            ) : (
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Transaction / cheque reference <span className="text-red-600">*</span>
                  </label>
                  <input
                    value={referenceNumber}
                    onChange={(e) => setReferenceNumber(e.target.value)}
                    placeholder="Enter transaction / cheque reference"
                    required
                    autoComplete="off"
                    className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment date <span className="text-red-600">*</span>
                  </label>
                  <input
                    type="date"
                    value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    required
                    className="w-full sm:w-56 rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:bg-white"
                  />
                </div>
                <div className="space-y-2">
                  <ProofAttachmentDropzone
                    file={null}
                    required={receipts.length === 0}
                    hint={receipts.length === 0 ? null : '(optional — add another)'}
                    busy={submitting}
                    onChange={(file) => {
                      if (file) onPickReceipt(file);
                    }}
                  />
                  {receipts.length > 0 && (
                    <ul className="space-y-1.5 text-xs text-gray-700">
                      {receipts.map((r, idx) => (
                        <li
                          key={`${r.fileId || r.fileName || 'receipt'}-${idx}`}
                          className="flex items-center justify-between gap-2 p-2 rounded-lg bg-gray-50 border border-gray-200"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="truncate font-medium text-gray-800">{r.originalName || r.fileName || 'Receipt uploaded'}</span>
                            <span className="text-gray-500 flex-shrink-0">({bytesToLabel(r.fileSize)})</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setReceipts((prev) => prev.filter((_, i) => i !== idx))}
                            className="inline-flex items-center text-red-600 hover:text-red-800 text-xs font-semibold px-2 py-0.5 rounded border border-red-200 hover:bg-red-50 transition-colors"
                            title="Remove attachment"
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleOfflineSubmit}
                  disabled={!canSubmitOffline}
                  className="px-4 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Submitting…' : 'Submit offline payment for approval'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {showTermsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setShowTermsModal(false)}
          />
          <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-xl border border-gray-200 max-h-[80vh] overflow-hidden">
            <div className="px-4 sm:px-5 py-3 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-900">
                {data.event?.name || 'SLIM Event'} - Payment Terms & Conditions
              </h3>
              <button
                type="button"
                onClick={() => setShowTermsModal(false)}
                className="text-gray-500 hover:text-gray-700 text-lg leading-none"
                aria-label="Close terms and conditions"
              >
                ×
              </button>
            </div>
            <div className="px-4 sm:px-5 py-4 overflow-y-auto text-sm text-gray-700">
              {paymentAgreementText ? (
                <p className="whitespace-pre-wrap leading-relaxed">{paymentAgreementText}</p>
              ) : (
                <ol className="ml-5 list-decimal space-y-1.5">
                  {defaultPaymentTerms.map((term, index) => (
                    <li key={index}>{term}</li>
                  ))}
                </ol>
              )}
            </div>
            <div className="px-4 sm:px-5 py-3 border-t border-gray-200 flex justify-end">
              <button
                type="button"
                onClick={() => setShowTermsModal(false)}
                className="px-3 py-1.5 rounded-md border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicProformaPaymentPage;
